import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getEprBaseUrl } from '../routes';
import { fetchActiveCycle } from '../services/reviewCycleService';
import { fetchFormsForReviewee } from '../services/reviewFormService';
import {
  getOrCreateSelfInvitation, markInvitationInProgress, submitResponse,
} from '../services/reviewInvitationService';
import OpticsScoringForm from '../components/review/OpticsScoringForm';
import StatusBadge from '../components/dashboard/StatusBadge';
import type { ReviewCycle, ReviewForm } from '../types';
import type { ReviewInvitation, ReviewResponseEntry, SelfNarrative } from '../types/magicLink';

// Login-based self-review surface using OPTICS 2026 framework.
// Auto-mints (or reuses) a 'self' invitation for the logged-in user, then
// shares the same submit path as the magic-link reviewer flow.

type ViewState = 'loading' | 'no_cycle' | 'no_form' | 'form' | 'submitting' | 'submitted' | 'already_done';

const draftKey = (formId: string) => `epr-self-draft-${formId}`;

interface Draft {
  entries: Record<string, ReviewResponseEntry>;
  selfNarrative: SelfNarrative;
  updatedAt: string;
}

const SelfReviewForm: React.FC = () => {
  const { user } = useAuth();
  const [state, setState] = useState<ViewState>('loading');
  const [cycle, setCycle] = useState<ReviewCycle | null>(null);
  const [form, setForm] = useState<ReviewForm | null>(null);
  const [invitation, setInvitation] = useState<ReviewInvitation | null>(null);
  const [entries, setEntries] = useState<Record<string, ReviewResponseEntry>>({});
  const [selfNarrative, setSelfNarrative] = useState<SelfNarrative>({});
  const [errorMsg, setErrorMsg] = useState('');
  const inProgressMarked = useRef(false);

  // ─── Init: load cycle + form + self-invitation ──────────────────────────
  useEffect(() => {
    if (!user) return;
    let alive = true;
    void (async () => {
      try {
        const c = await fetchActiveCycle();
        if (!alive) return;
        if (!c) { setState('no_cycle'); return; }
        setCycle(c);

        const forms = await fetchFormsForReviewee(user.id);
        if (!alive) return;
        const myForm = forms.find(f => f.cycleId === c.id) ?? null;
        if (!myForm) { setState('no_form'); return; }
        setForm(myForm);

        const inv = await getOrCreateSelfInvitation(
          myForm,
          { id: user.id, name: user.name ?? '', email: user.email ?? '' },
          getEprBaseUrl(),
        );
        if (!alive) return;
        setInvitation(inv);

        if (inv.status === 'submitted') { setState('already_done'); return; }

        // Hydrate draft
        try {
          const raw = localStorage.getItem(draftKey(myForm.id));
          if (raw) {
            const d = JSON.parse(raw) as Draft;
            if (d.entries) setEntries(d.entries);
            if (d.selfNarrative) setSelfNarrative(d.selfNarrative);
          }
        } catch { /* ignore */ }

        setState('form');
      } catch (e) {
        console.error('[SelfReviewForm] init failed:', e);
        setErrorMsg((e as Error).message ?? String(e));
        setState('form');
      }
    })();
    return () => { alive = false; };
  }, [user]);

  // ─── Auto-save draft ────────────────────────────────────────────────────
  useEffect(() => {
    if (state !== 'form' || !form) return;
    const draft: Draft = { entries, selfNarrative, updatedAt: new Date().toISOString() };
    try { localStorage.setItem(draftKey(form.id), JSON.stringify(draft)); } catch { /* quota */ }
  }, [entries, selfNarrative, state, form]);

  // ─── Mark in-progress on first interaction ──────────────────────────────
  useEffect(() => {
    if (!invitation || inProgressMarked.current) return;
    if (Object.keys(entries).length === 0) return;
    inProgressMarked.current = true;
    void markInvitationInProgress(invitation.id);
  }, [entries, invitation]);

  // ─── Submit ─────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!invitation) return;
    setState('submitting');
    setErrorMsg('');
    try {
      await submitResponse({
        invitation,
        entries: Object.values(entries),
        selfNarrative,
      });
      if (form) localStorage.removeItem(draftKey(form.id));
      setState('submitted');
    } catch (e) {
      setErrorMsg((e as Error).message ?? String(e));
      setState('form');
    }
  };

  const cycleLabel = useMemo(() => cycle ? `${cycle.name}` : '—', [cycle]);

  // ─── Render: terminal states ────────────────────────────────────────────
  if (state === 'loading') {
    return <div className="p-8 text-stone-500 text-sm">Loading your self-review…</div>;
  }
  if (state === 'no_cycle') {
    return (
      <CenterCard tone="info" icon="📭" title="No active review cycle">
        Wait for HR to open the next cycle, or check the Dashboard for updates.
      </CenterCard>
    );
  }
  if (state === 'no_form') {
    return (
      <CenterCard tone="warn" icon="⚠️" title="You're not enrolled in this cycle">
        Contact HR if you should be included. Once enrolled, your self-review will appear here automatically.
      </CenterCard>
    );
  }
  if (state === 'already_done') {
    return (
      <CenterCard tone="success" icon="✅" title="Self-review already submitted">
        Your self-review for <b>{cycleLabel}</b> is locked in. Your line manager &amp; review panel will see the aggregated result.
        <div className="mt-4">
          <Link to="/dashboard" className="inline-block px-4 py-2 text-sm font-medium bg-stone-900 text-white rounded-md hover:bg-stone-800">← Back to dashboard</Link>
        </div>
      </CenterCard>
    );
  }
  if (state === 'submitted') {
    return (
      <CenterCard tone="success" icon="🎉" title="Self-review submitted">
        Thank you. Your self-assessment is in. HR will dispatch your review panel (line manager · peers · direct reports) if not already running.
        <div className="mt-4">
          <Link to="/dashboard" className="inline-block px-4 py-2 text-sm font-medium bg-stone-900 text-white rounded-md hover:bg-stone-800">← Back to dashboard</Link>
        </div>
      </CenterCard>
    );
  }

  // ─── Render: form ───────────────────────────────────────────────────────
  return (
    <div className="max-w-3xl mx-auto p-6">
      {/* Header */}
      <div className="bg-white border border-stone-200 rounded-xl p-5 mb-4 shadow-sm">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-stone-500 mb-1">Your Self-Review</div>
            <h1 className="text-xl font-semibold text-stone-900 tracking-tight">{user?.name ?? 'You'}</h1>
            <p className="text-xs text-stone-600 mt-1">
              {cycleLabel}
              {' · '}OPTICS 2026 framework
              {' · '}<span className="text-emerald-700">Auto-saved</span>
            </p>
          </div>
          {form && <StatusBadge status={form.status} />}
        </div>
        <p className="text-[11px] text-stone-500 mt-3 leading-relaxed">
          Rate yourself across the 6 OPTICS values × 3 contribution lenses (Individual / Team / Company).
          Section 7 at the end is a short reflection — yours alone.
        </p>
      </div>

      <OpticsScoringForm
        entries={entries}
        setEntries={setEntries}
        selfNarrative={selfNarrative}
        setSelfNarrative={setSelfNarrative}
        isSelf={true}
        onSubmit={handleSubmit}
        submitting={state === 'submitting'}
        submitLabel="Submit self-review ✓"
        errorMsg={errorMsg}
      />

      <p className="text-[11px] text-stone-500 text-center mt-6">
        Confidential — internal use only. Once submitted you cannot edit your self-review.
      </p>
    </div>
  );
};

// ─── Small terminal-state card ────────────────────────────────────────────

const CenterCard: React.FC<{
  tone: 'info' | 'success' | 'warn' | 'error';
  icon: string;
  title: string;
  children: React.ReactNode;
}> = ({ tone, icon, title, children }) => {
  const toneCls = {
    info:    'bg-stone-50    border-stone-200    text-stone-900',
    success: 'bg-emerald-50  border-emerald-200  text-emerald-900',
    warn:    'bg-amber-50    border-amber-200    text-amber-900',
    error:   'bg-red-50      border-red-200      text-red-900',
  }[tone];
  return (
    <div className="max-w-xl mx-auto p-6">
      <div className={`border rounded-xl p-8 text-center ${toneCls}`}>
        <div className="text-4xl mb-2">{icon}</div>
        <h2 className="text-lg font-semibold tracking-tight mb-2">{title}</h2>
        <div className="text-sm leading-relaxed">{children}</div>
      </div>
    </div>
  );
};

export default SelfReviewForm;
